import "./tabsHomePage.css";
import React, {
  useState,
  useEffect,
  useContext,
  Suspense,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { TabContext, TabList } from "@mui/lab";
import { Tab, Grid2 as Grid, IconButton, Tooltip } from "@mui/material";
import { Close } from "@mui/icons-material";
import ScrollToTop from "react-scroll-to-top";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  NavigateContext,
  M_StateActions,
  MLateralMenu,
  LoadingBar,
  MButton,
} from "src/M_Components";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PropTypes from "prop-types";

import { STORAGE_CARRIER, STORAGE_USER } from "src/components/auth/storageKeys";
import { useCarrierAuth } from "src/components/auth/CarrierAuthProvider";
import { useUserAuth } from "src/components/auth/UserAuthProvider";

// Pages ...
import ChangeLogPage from "src/pages/Logs/LogPage";
import SettingsPage from "../settings/SettingsPage";
import UserConfigCrud from "src/pages/Users/userConfigCrud";
import RoleConfigCrud from "src/pages/Roles/roleConfigCrud";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { hasPermission } from "src/components/utils/permissionsHelper";
import { ModuleActionEnumReverse } from "src/M_Components/M_Utils/permissions";
import AccountPage from "src/pages/Accounts/accountConfig";
import AccountConfigCrud from "src/pages/Accounts/AccountConfigCrud";
import ContactPage from "src/pages/Contacts/ContactPage";
import ContactConfigCrud from "src/pages/Contacts/ContactConfigCrud";
import ShipperConfigPage from "src/pages/Shippers/ShipperConfigPage";
import ShipperMarketManagementPage from "src/pages/Shippers/ShipperMarketManagementPage";
import ShipperCarrierManagementPage from "src/pages/Shippers/shipperCarrierManagementPage";
import ShipperConfigCrud from "src/pages/Shippers/shipperConfigCrud";
import CarrierConfigPage from "src/pages/Carriers/carrierConfigPage";
import CarrierLoginsConfigPage from "src/pages/Carriers/CarrierLoginsConfigPage";
import CarrierMarketManagementPage from "src/pages/Carriers/CarrierMarketManagementPage";
import CarrierTypeOfServiceManagementPage from "src/pages/Carriers/CarrierTypeOfServiceManagementPage";
import CarrierLoginsConfigCrud from "src/pages/Carriers/CarrierLoginsConfigCrud";
import CarrierConfigCrud from "src/pages/Carriers/carrierConfigCrud";
import MarketPage from "src/pages/Markets/marketPage";
import MarketConfigCrud from "src/pages/Markets/marketConfigCrud";
import LocationPage from "src/pages/Locations/locationPage";
import LocationConfigCrud from "src/pages/Locations/locationConfigCrud";
import CountryPage from "src/pages/Countries/countryPage";
import CountryConfigCrud from "src/pages/Countries/countryConfigCrud";
import ContinentConfigCrud from "src/pages/Countries/continentConfigCrud";
import CurrencyPage from "src/pages/Currencies/currencyPage";
import CurrencyConfigCrud from "src/pages/Currencies/currencyConfigCrud";
import TransportTypePage from "src/pages/TransportTypes/transportTypePage";
import TransportTypeConfigCrud from "src/pages/TransportTypes/transportTypeConfigCrud";
import LanePage from "src/pages/Lanes/lanePage";
import LaneConfigCrud from "src/pages/Lanes/laneConfigCrud";
import RoutePage from "src/pages/Routes/routePage";
import RouteConfigCrud from "src/pages/Routes/routeConfigCrud";
import RouteLaneConfigCrud from "src/pages/Routes/routeLaneConfigCrud";
import InstantQuotesSearchPage from "src/pages/InstantRequestForQuotes/InstantQuotesPage";
import InstantQuotesSearchConfigCrud from "src/pages/InstantRequestForQuotes/InstantQuotesSearchConfigCrud";
import InstantQuotesConfigCrud from "src/pages/InstantRequestForQuotes/InstantQuotesConfigCrud";
import SpotTenderPage from "src/pages/SpotTenders/SpotTenderPage";
import NonSpotTenderPage from "src/pages/NonSpotTenders/NonSpotTenderPage";
import CreateTenderPage from "src/pages/CreateTenders/CreateTenderPage";
import TenderInQuotationPage from "src/pages/Tenders/TenderInQuotationPage";
import TenderFinishedPage from "src/pages/Tenders/TenderFinishedPage";
import TenderPhaseConfig from "src/pages/NonSpotTenders/TenderPhaseConfig";
import AddressPage from "src/pages/Addresses/AddressPage";
import AddressConfigCrud from "src/pages/Addresses/AddressConfigCrud";
import ApiKeyConfigCrud from "src/pages/ApiKeys/ApiKeyConfigCrud";
import TenderConfigCrud from "src/pages/Tenders/TenderConfigCrud";
import TrackingPage from "src/pages/Tracking/TrackingPage";
import TrackingConfigCrud from "src/pages/Tracking/TrackingConfigCrud";

// Carrier invite / bid pages
import SpotTenderCarrierInvitePage from "src/pages/CarrierInvitation/SpotTender/SpotTenderCarrierInvitePage";
import NonSpotTenderCarrierInvitePage from "src/pages/CarrierInvitation/NonSpotTender/NonSpotTenderCarrierInvitePage";
import CarrierSubmitPage from "src/pages/CarrierInvitation/CarrierSubmitPage";
import CarrierInviteViewBidPage from "src/pages/CarrierInvitation/CarrierInviteViewBidPage";

/* =============================================================================
   Utility helpers for stability / performance
   ========================================================================== */

const jsonEqual = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const setIfChanged = (setter, nextVal) =>
  setter((prev) => (jsonEqual(prev, nextVal) ? prev : nextVal));

/**
 * Deep clone helper for menu trees.
 * Defensive to avoid mutating shared references across re-renders.
 */
function clone(arr) {
  try {
    return JSON.parse(JSON.stringify(arr || []));
  } catch {
    return [];
  }
}

/**
 * Prefix internal MenuLabelUrl paths with "/carrier/home" for carrier realm
 * unless they already point to the carrier namespace or to an external URL.
 *
 * Important:
 * - HashRouter injects "#" automatically; MenuLabelUrl should never contain "#".
 *
 * Examples:
 *   "/SpotTenderQuotes" -> "/carrier/home/SpotTenderQuotes"
 *   "https://external"  -> stays unchanged
 *   "/carrier/home/xyz" -> stays unchanged
 */
function remapCarrierMenuUrls(menus) {
  const PREFIX = "/carrier/home";
  const src = clone(menus);

  const patchUrl = (u) => {
    if (!u || typeof u !== "string") return u;

    // Already a carrier path
    if (u.startsWith("/carrier")) return u;

    // Absolute external links
    if (
      /^[a-zA-Z]+:\/\//.test(u) ||
      u.startsWith("mailto:") ||
      u.startsWith("tel:")
    ) {
      return u;
    }

    // Internal path ("/...") → prefix with carrier home
    if (u.startsWith("/")) return `${PREFIX}${u}`;

    // Any other case, leave as-is
    return u;
  };

  const walk = (items) => {
    (items || []).forEach((m) => {
      m.MenuLabelUrl = patchUrl(m.MenuLabelUrl);
      if (Array.isArray(m.SubMenus)) walk(m.SubMenus);
    });
  };

  walk(src);
  return src;
}

/* =============================================================================
   Tab label helper:
   - Fixed visual width inside the Tab
   - Ellipsis when text overflows
   - Marquee animation when overflowing (hover / selected)
   - Uses ResizeObserver so it reacts to:
     * window resize
     * i18n label changes
     * any layout change affecting width
   ========================================================================== */
function TabLabel({ text }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const label = textRef.current;
    if (!container || !label) return;

    const checkOverflow = () => {
      // Compare the real width of the text with the visible width of the container
      const overflowing = label.scrollWidth > container.clientWidth + 1;
      setIsOverflowing(overflowing);
    };

    checkOverflow();

    // Prefer ResizeObserver for precise re-measurement
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(checkOverflow);
      ro.observe(container);
      ro.observe(label);
      return () => ro.disconnect();
    }

    // Fallback: listen to window resize
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [text]);

  return (
    <span
      ref={containerRef}
      className={`tabLabelContainer ${isOverflowing ? "tabLabel--scroll" : ""}`}
    >
      <span ref={textRef} className="tabLabelText">
        {text}
      </span>
    </span>
  );
}

/* =============================================================================
   Main layout component (realm-aware)
   ========================================================================== */
export default function TabsHomePage({ roles }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ctx = useContext(LoadingContext);

  const { logout: carrierLogout } = useCarrierAuth();
  const { logout: userLogout } = useUserAuth();

  const dispatch = useDispatch();
  const M_StateData = useSelector((state) => state.StateKeyValue);

  const [showMenu, setShowMenu] = useState(false);
  const [menus, _setMenus] = useState([]);

  const [defaultAccountName, setDefaultAccountName] = useState("");
  const [defaultShipperName, setDefaultShipperName] = useState("");
  const [carrierAccountName, setCarrierAccountName] = useState("");
  const [carrierName, setCarrierName] = useState("");
  const [cargofiveApiKeyId, setCargofiveApiKeyId] = useState("");

  const [selectedTab, setSelectedTab] = useState("menu");
  const [lastOpenTab, setLastOpenTab] = useState("");
  const [tabs, setTabs] = useState([]);
  const [tabIndex, setTabIndex] = useState(1);

  const [showButtons, setShowButtons] = useState(true);

  const validTabValues = useMemo(
    () => ["menu", ...tabs.map((t) => String(t.value))],
    [tabs]
  );

  const uiTabValue = useMemo(() => {
    const current = String(selectedTab);
    if (validTabValues.includes(current)) return current;
    if (validTabValues.length > 0) return String(validTabValues[0]);
    return "menu";
  }, [selectedTab, validTabValues]);

  const [isFirstTime, setIsFirstTime] = useState(true);

  const IS_CARRIER = pathname.startsWith("/carrier");
  const BASE = IS_CARRIER ? "/carrier/home" : "/home";

  const bufferServiceMenu = useMemo(
    () => new BufferService("Menus/All", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const checkPermissions = useCallback(
    (moduleName, actionName) => hasPermission(roles, moduleName, actionName),
    [roles]
  );

  const setMenus = useCallback((val) => setIfChanged(_setMenus, val), []);

  /* ---------------------------------------------------------------------------
     Boot: tenant / carrier badges
     ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!IS_CARRIER) {
      const dAcc = localStorage.getItem("defaultAccountName");
      const dShip = localStorage.getItem("defaultShipperName");
      const cfid = localStorage.getItem("cargofiveApiKeyId");
      if (dAcc !== null) setDefaultAccountName(dAcc);
      if (dShip !== null) setDefaultShipperName(dShip);
      if (cfid !== null) setCargofiveApiKeyId(cfid);
      return;
    }

    try {
      const raw = localStorage.getItem(STORAGE_CARRIER?.profile);
      if (!raw) return;
      const parsed = JSON.parse(raw) || {};
      const accName = parsed?.account?.Name ?? "";
      const carName = parsed?.carrier?.Name ?? "";
      setCarrierAccountName(accName);
      setCarrierName(carName);
    } catch {
      // ignore parse errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, IS_CARRIER]);

  /* ---------------------------------------------------------------------------
     Rehydrate UI + menus from Redux/localStorage on first load
     - Now realm-aware:
       * Carrier realm reads menus from STORAGE_CARRIER.menus (carrier-only key)
       * User realm reads menus from "Menus" (shared user key)
     ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!isFirstTime) return;

    const tbs = M_StateData.find((obj) => obj.key === "tabs")?.value;
    const sltbs = M_StateData.find((obj) => obj.key === "selectedTab")?.value;
    const swbts = M_StateData.find((obj) => obj.key === "showButtons")?.value;
    const tabI = M_StateData.find((obj) => obj.key === "tabIndex")?.value;
    const lOT = M_StateData.find((obj) => obj.key === "lastOpenTab")?.value;

    // ---------- MENUS: separate storage per realm ----------
    if (IS_CARRIER) {
      // Carrier realm: menus are stored under STORAGE_CARRIER.menus
      try {
        const rawCarrierMenus = localStorage.getItem(STORAGE_CARRIER.menus);
        if (rawCarrierMenus) {
          const parsed = JSON.parse(rawCarrierMenus);
          const carrierMenus = remapCarrierMenuUrls(parsed);
          setMenus(carrierMenus);
        }
      } catch {
        // Ignore parse errors; menus will remain []
      }
    } else {
      // User realm: still uses "Menus" shared key
      const menusLS = localStorage.getItem(STORAGE_USER.menus);
      if (menusLS) {
        try {
          const parsed = JSON.parse(menusLS);
          setMenus(parsed);
        } catch {
          // ignore parse errors; backend will handle later
        }
      }
    }

    if (tbs !== undefined) setIfChanged(setTabs, tbs);
    if (sltbs !== undefined) setSelectedTab(sltbs);
    if (swbts !== undefined) setShowButtons(swbts);
    if (tabI !== undefined) setTabIndex(tabI);
    if (lOT !== undefined) setLastOpenTab(lOT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_StateData, isFirstTime, IS_CARRIER]);

  /* ---------------------------------------------------------------------------
     Menus fetch strategy (USER realm only):
     Plan A (Storage "Menus") -> Plan B (Backend Menus/All)
     - Carrier realm no longer touches "Menus" to avoid cross-tab contamination.
     ------------------------------------------------------------------------ */
  useEffect(() => {
    // In carrier realm, menus come from STORAGE_CARRIER.menus only.
    if (IS_CARRIER) return;

    let alive = true;
    const menusLS =
      localStorage.getItem(STORAGE_USER.menus);
    if (menusLS) {
      try {
        const parsed = JSON.parse(menusLS);
        if (alive) {
          setMenus(parsed);
        }
        return () => {
          alive = false;
        };
      } catch {
        // invalid LS data, fall through to backend
      }
    }

    bufferServiceMenu.get().then((res) => {
      if (!alive) return;
      if (res?.name === "AxiosError") {
        ctx.toastMessages("error", "", t("ErrorLoadingData"));
        return;
      }
      try {
        localStorage.setItem(STORAGE_USER.menus, JSON.stringify(res));
      } catch {
        // ignore storage errors
      }
      setMenus(res);
    });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IS_CARRIER]);

  /* ---------------------------------------------------------------------------
   Initial route handling + persist Redux snapshots
   ------------------------------------------------------------------------ */
  useEffect(() => {
    if (isFirstTime) {
      const id = setTimeout(() => {
        const alreadyOnChildRoute =
          pathname.startsWith(`${BASE}/`) && pathname.length > BASE.length + 1;

        // Deep-link support:
        // If we are already on BASE/child, normally we create the initial tab.
        if (alreadyOnChildRoute) {
          const tabUrl = pathname.replace(`${BASE}/`, "");
          const routeKey = tabUrl.split(/[?#/]/)[0];

          // --------------------------------------------------------------------
          // EXCEPTION: Carrier invite technical route
          //
          // When the user enters via an invite link:
          //   /carrier/home/CarrierInviteViewBid?...
          //
          // That route is only a "wrapper" to resolve context and then
          // programmatically open the real Tender tab.
          //
          // If we create a tab here, we end up with TWO tabs:
          //   1) "CarrierInviteViewBid"  (wrong / technical)
          //   2) "Tender X - Phase Y"    (correct)
          //
          // So in this specific case we DO NOT auto-create a tab.
          // We let CarrierInviteViewBidPage handle the correct tab creation.
          // --------------------------------------------------------------------
          if (IS_CARRIER && routeKey === "CarrierInviteViewBid") {
            // Stay in the current route but do not create a dynamic tab now.
            // The invite flow will open the correct tender tab shortly after.
            setSelectedTab("menu");
            setShowButtons(false); // Hide landing screen buttons
          } else {
            // Normal deep-link behavior: create the initial dynamic tab
            const translated = t(routeKey);
            const isTranslated = translated && translated !== routeKey;
            const translatedLabel = isTranslated ? translated : routeKey;

            const initialTab = {
              value: "1",
              label: translatedLabel,
              tabUrl,
              i18nKey: routeKey,
            };

            setTabs([initialTab]);
            setSelectedTab("1");
            setTabIndex(2);
            setShowButtons(false);
            setLastOpenTab("1");
          }
        } else {
          // Default behavior: go to BASE menu view.
          setSelectedTab("menu");
          navigate(BASE);
          setShowButtons(true);
          setLastOpenTab("");
        }

        setIsFirstTime(false);
      }, 50);

      return () => clearTimeout(id);
    } else {
      // After the first render, keep Redux snapshot in sync with current UI state.
      dispatch(M_StateActions.addM_State({ key: "tabs", value: tabs }));
      dispatch(
        M_StateActions.addM_State({
          key: "selectedTab",
          value: selectedTab,
        })
      );
      dispatch(
        M_StateActions.addM_State({
          key: "showButtons",
          value: showButtons,
        })
      );
      dispatch(
        M_StateActions.addM_State({
          key: "tabIndex",
          value: tabIndex,
        })
      );
      dispatch(
        M_StateActions.addM_State({
          key: "lastOpenTab",
          value: lastOpenTab,
        })
      );
    }
  }, [isFirstTime, pathname, tabs, selectedTab, showButtons, tabIndex, lastOpenTab, dispatch, navigate, BASE, t, IS_CARRIER]);

  /* ---------------------------------------------------------------------------
     Handlers
     ------------------------------------------------------------------------ */

  /**
   * Global logout handler:
   * Clears auth state from the correct provider (user or carrier)
   * Clears only the current realm's persisted storage (preserves other realm)
   * Preserves "lng"
   * Clears Redux state for tabs and UI memory
   * Redirects to realm entry:
   *    - Carrier -> "/carrier-inviting"
   *    - User    -> "/login"
   * 
   */
  const handleLogout = useCallback(() => {
    ctx.setIsLoad(true);

    // Preserve language preference across logout
    const currentLng = localStorage.getItem("lng");

    // Clear auth + persisted data for the active realm only
    if (IS_CARRIER && typeof carrierLogout === "function") {
      try {
        carrierLogout({ clearStorage: true });
      } catch {
        // ignore logout errors
      }
    } else if (!IS_CARRIER && typeof userLogout === "function") {
      try {
        userLogout({ clearStorage: true });
      } catch {
        // ignore logout errors
      }
    }

    // Reset in-memory UI state
    setShowMenu(false);
    setTabs([]);
    setSelectedTab("menu");
    setLastOpenTab("");
    setTabIndex(1);

    // Restore language preference (do NOT localStorage.clear())
    if (currentLng) localStorage.setItem("lng", currentLng);

    // Clear persisted UI state for this runtime (next step is to scope persist per realm)
    dispatch(M_StateActions.DeleteAll_M_State());

    // Redirect to the correct realm entry point
    navigate(IS_CARRIER ? "/carrier-inviting" : "/login", {
      replace: true,
    });

    // Small delay to avoid loader flicker during route transition
    setTimeout(() => ctx.setIsLoad(false), 250);
  }, [IS_CARRIER, carrierLogout, userLogout, ctx, dispatch, navigate]);


  /**
   * Normalize any local route to the correct BASE:
   * - If url is empty or "/" or BASE -> return BASE.
   * - If url already starts with BASE -> return as-is.
   * - If url starts with "/home" or "/carrier/home" -> return as-is.
   * - If url starts with "/" -> glue after BASE ("/home" or "/carrier/home").
   * - Otherwise treat as a bare segment and prefix with `${BASE}/`.
   */
  const toBase = useCallback(
    (url) => {
      let result;
      if (!url || url === "/" || url === BASE) result = BASE;
      else if (url.startsWith(BASE)) result = url;
      else if (url.startsWith("/carrier/home") || url.startsWith("/home"))
        result = url;
      else if (url.startsWith("/")) result = `${BASE}${url}`;
      else result = `${BASE}/${url}`;
      return result;
    },
    [BASE]
  );

  /**
   * Open a given route into a new tab (and navigate to it).
   * - url can be absolute or relative to BASE.
   * - passLabel optionally overrides the initial tab label.
   * - event is used to detect middle-click (do not navigate in that case).
   *
   * IMPORTANT: we always set i18nKey = routeKey so that language changes
   * can re-translate the label later, regardless of passLabel.
   */
  const goToPage = useCallback(
    (url, passLabel, event) => {
      const normalizedUrl =
        url.startsWith("/home") || url.startsWith("/carrier/home")
          ? url.replace(/^\/(carrier\/home|home)\//, "")
          : url.startsWith("/")
            ? url.substring(1)
            : url;

      const routeKey = normalizedUrl.split(/[?#/]/)[0];

      // Translation key: if you pass a label, use that; otherwise, use the route
      const translationKey = passLabel || routeKey;

      const translated = t(translationKey);
      const isTranslated = translated && translated !== translationKey;

      const label = isTranslated ? translated : translationKey;

      const nextValue = String(tabIndex);

      // Navigate (unless middle-click)
      if (event?.button !== 1) {
        navigate(toBase(url));
        setShowButtons(false);
        setShowMenu(false);
        setSelectedTab(nextValue);
      }

      setTabs((prev) => {
        const newTab = {
          value: nextValue,
          label,
          tabUrl: normalizedUrl,
          i18nKey: translationKey, // store routeKey for future i18n updates
        };
        return [...prev, newTab];
      });

      dispatch(
        M_StateActions.addM_State({
          key: "selectedTab",
          value: nextValue,
        })
      );

      setTabIndex((prev) => prev + 1);
      setLastOpenTab(String(selectedTab));

      ctx.toastMessages("success", "", t("MessageTabCreated"));
    },
    [dispatch, navigate, toBase, tabIndex, selectedTab, ctx, t]
  );

  /**
   * Navigate back to the realm landing menu (pseudo-tab "menu").
   */
  const menusRoute = useCallback(() => {
    setShowButtons(true);
    setShowMenu(true);
    setSelectedTab("menu");
    navigate(BASE);
  }, [navigate, BASE]);

  /**
   * Logo click:
   * - If the menu is closed -> have like Home (go to BASE/menu and open menu).
   * - If the menu is open   -> just close the menu (keep current route/tab).
   */
  const handleLogoClick = useCallback(() => {
    if (!showMenu) {
      menusRoute();
    } else {
      setShowMenu(false);
    }
  }, [showMenu, menusRoute]);

  /**
   * Navigate to a specific tab URL (realm-aware).
   * Used when selecting a tab from the TabList.
   */
  const tabsRoutes = useCallback(
    (url) => {
      setShowMenu(false);
      setShowButtons(false);
      setLastOpenTab(String(selectedTab));
      navigate(toBase(url));
    },
    [navigate, toBase, selectedTab]
  );

  /**
   * TabList onChange handler:
   * - Updates selectedTab
   * - Navigates to the corresponding route:
   *    * "menu" -> menusRoute()
   *    * other -> tabsRoutes(tab.tabUrl)
   */
  const handleChange = useCallback(
    (event, newValue) => {
      setSelectedTab(newValue);

      dispatch(
        M_StateActions.addM_State({
          key: "selectedTab",
          value: newValue,
        })
      );

      if (newValue === "menu") {
        menusRoute();
      } else {
        const targetTab = tabs.find(
          (t) => String(t.value) === String(newValue)
        );
        if (targetTab) {
          tabsRoutes(targetTab.tabUrl);
        }
      }
    },
    [dispatch, tabs, tabsRoutes, menusRoute]
  );

  /**
   * Renames an existing tab by internal value (stringified).
   * Useful for i18n changes, or when a CRUD page wants to rename the tab caption.
   */
  const renameTab = useCallback((value, newLabel) => {
    setTabs((prev) =>
      prev.map((t) =>
        String(t.value) === String(value) ? { ...t, label: newLabel } : t
      )
    );
  }, []);

  /**
   * Close the tab identified by "value"; gracefully fall back to:
   * - The lastOpenTab (if still present), or
   * - The menu (landing) view when no tabs remain.
   *
   * When "value" is not provided, the currently selected tab is closed.
   * Child pages can simply call handleTabClose() with no args to close themselves.
   */
  const handleTabClose = useCallback(
    (value) => {
      // Show global loading spinner (owned by MRouter via LoadingContext)
      ctx.setIsLoad(true);

      // If caller didn't pass a tab id, close the currently selected tab
      const rawValue =
        value === undefined || value === null ? selectedTab : value;

      // Normalize tab id to a string (tabs store string values)
      const v = typeof rawValue === "string" ? rawValue : `${rawValue}`;

      // Remove per-tab state stored under the tab key
      dispatch(M_StateActions.removeM_State({ key: v }));

      // Some tabs also persist temporary table state under "{tabId}.1" and "{tabId}.2"
      // (kept as legacy compatibility with existing pages)
      const tempDbTable = M_StateData.find((obj) => obj.key === `"${v}.1"`);
      const tempDbTableChild = M_StateData.find((obj) => obj.key === `"${v}.2"`);

      if (tempDbTable) {
        dispatch(M_StateActions.removeM_State({ key: `"${v}.1"` }));
      }
      if (tempDbTableChild) {
        dispatch(M_StateActions.removeM_State({ key: `"${v}.2"` }));
      }

      // Update the tabs list (pure update). Side effects must NOT happen here,
      // otherwise React can warn about updating other components during render.
      setTabs((prevTabs) => {
        const remainingTabs = prevTabs.filter((t) => t.value !== v);

        // Defer side effects (routing / toasts / loading off) to the next tick
        // so this updater stays pure and does not trigger cross-component updates.
        setTimeout(() => {
          // Case A: no tabs left -> return to menu and cleanup any leftover state
          if (remainingTabs.length === 0) {
            setSelectedTab("menu");
            menusRoute();

            // Remove all "non-core" persisted keys (keep only global UI keys)
            const errorKeys = M_StateData.filter(
              (obj) =>
                obj.key !== "tabs" &&
                obj.key !== "selectedTab" &&
                obj.key !== "showButtons" &&
                obj.key !== "tabIndex" &&
                obj.key !== "lastOpenTab"
            );
            errorKeys.forEach((k) =>
              dispatch(M_StateActions.removeM_State({ key: k.key }))
            );

            // Notify user and stop loading
            ctx.toastMessages("success", "", t("MessageTabClosed"));
            ctx.setIsLoad(false);
            return;
          }

          // Case B: there are still tabs open
          // Prefer restoring the lastOpenTab if it still exists
          const lastTab = remainingTabs.find(
            (t) => String(t.value) === String(lastOpenTab)
          );

          // Only change route/selection if we are closing the currently active tab.
          // If we're closing a background tab, the current view should remain as-is.
          if (String(selectedTab) === String(v)) {
            if (lastTab) {
              // Navigate to the last open tab and mark it as selected
              tabsRoutes(lastTab.tabUrl);
              setSelectedTab(String(lastTab.value));
            } else {
              // Fallback: go back to menu if lastOpenTab is missing
              setSelectedTab("menu");
              menusRoute();
            }
          }

          // Notify user and stop loading
          ctx.toastMessages("success", "", t("MessageTabClosed"));
          ctx.setIsLoad(false);
        }, 0);

        return remainingTabs;
      });
    },
    [
      M_StateData,
      dispatch,
      lastOpenTab,
      selectedTab,
      ctx,
      t,
      menusRoute,
      tabsRoutes,
    ]
  );


  /**
   * Change current language and persist to localStorage.
   */
  const handleChangeLng = useCallback(
    (lng) => {
      i18n.changeLanguage(lng);
      localStorage.setItem("lng", lng);
    },
    [i18n]
  );

  /**
   * Routed from user settings menu toggle.
   * Opens the app settings page in a new tab (i18n-aware via routeKey "settings").
   */
  const handleSettings = useCallback(() => {
    // Let goToPage compute the translated label based on "settings" key
    goToPage("settings");
  }, [goToPage]);

  /**
   * Routed from MLateralMenu clicks.
   * Opens the target route as a new tab using the shared tab system.
   * We ignore the "label" parameter on purpose to keep i18n consistent:
   * the route key will be used for tab label translation.
   */
  const handleMenuPages = useCallback(
    (route, label) => {
      goToPage(route, label);
    },
    [goToPage]
  );

  /* ---------------------------------------------------------------------------
     Filter menus by permission (memoized)
     ------------------------------------------------------------------------ */
  const filteredMenus = useMemo(() => {
    // If roles are not ready yet, do NOT hide everything.
    // Render raw menus first; once roles arrive, the list will be filtered.
    if (!roles || (Array.isArray(roles) && roles.length === 0)) {
      return menus || [];
    }

    const result = (menus || [])
      .filter((menu) => {
        const actionValue = Number(menu.Action);
        const actionName = !isNaN(actionValue)
          ? ModuleActionEnumReverse[actionValue]
          : null;
        return checkPermissions(menu.ModuleName, actionName);
      })
      .map((menu) => ({
        ...menu,
        SubMenus: (menu.SubMenus || [])
          .filter((sub) => {
            const actionValue = Number(sub.Action);
            const actionName = !isNaN(actionValue)
              ? ModuleActionEnumReverse[actionValue]
              : null;
            return checkPermissions(sub.ModuleName, actionName);
          })
          .map((sub) => ({
            ...sub,
            SubMenus: (sub.SubMenus || []).filter((subsub) => {
              const actionValue = Number(subsub.Action);
              const actionName = !isNaN(actionValue)
                ? ModuleActionEnumReverse[actionValue]
                : null;
              return checkPermissions(subsub.ModuleName, actionName);
            }),
          })),
      }));

    return result;
  }, [menus, roles, checkPermissions]);


  /* ---------------------------------------------------------------------------
     Re-translate all open tabs when language changes
     ------------------------------------------------------------------------ */
  useEffect(() => {
    setTabs((prev) =>
      prev.map((tab) => {
        if (!tab.i18nKey) return tab;

        const translated = t(tab.i18nKey);

        const hasValidTranslation =
          translated &&
          typeof translated === "string" &&
          translated.trim().length > 0 &&
          translated !== tab.i18nKey;

        if (!hasValidTranslation) {
          return tab;
        }

        const nextLabel = translated.trim();

        if (nextLabel === tab.label) return tab;

        return { ...tab, label: nextLabel };
      })
    );
  }, [i18n.language, t]);

  /**
   * Value exposed via NavigateContext so that child pages can:
   * - open a new tab (newTab)
   * - close the current tab (closeTab)
   * - rename an existing tab (renameTab)
   */
  const contextCallback = useMemo(
    () => ({ newTab: goToPage, closeTab: handleTabClose, renameTab }),
    [goToPage, handleTabClose, renameTab]
  );

  /* ---------------------------------------------------------------------------
     Render
     ------------------------------------------------------------------------ */
  return (
    // Expose tab navigation helpers to all descendant pages
    <NavigateContext.Provider value={contextCallback}>
      {/* Top bar: logo + burger + dynamic tabs + account badges */}
      <Grid
        container
        className="divTabs"
        style={{
          "--tabs-bg": ctx.secondaryColor || "var(--color-red-dark)",
          "--tabs-fg": "var(--color-white)",
          "--tabs-selected-fg": ctx.secondaryColor || "var(--color-black)",
        }}
      >
        {/* Left side: logo + burger menu */}
        <Grid
          size={{ xs: 0.5, sm: 1.0, md: 1.0, lg: 1 }}
          className="wrapperLogoMenu"
        >
          <MButton
            none
            noHover
            backgroundColor={ctx.secondaryColor || "var(--color-red-dark)"}
            tooltip={t("HomePage")}
            onClick={handleLogoClick}
            icon={
              <img
                className="logoImage"
                src="./images/VectorShipperForm.png"
                alt="home"
              />
            }
          />
          <Tooltip title="Menu">
            <IconButton
              aria-haspopup="true"
              size="large"
              disableRipple={true}
              onClick={() => setShowMenu((s) => !s)}
              className="menuIconStyle"
            >
              <i className="buttonFavorites pi pi-bars" />
            </IconButton>
          </Tooltip>
        </Grid>

        {/* Center: dynamic tabs (menu + opened pages) */}
        <Grid size={{ xs: 4.3, sm: 7.5, md: 7.5, lg: 9 }}>
          <TabContext value={uiTabValue}>
            <TabList
              scrollButtons={tabs?.length !== 0}
              allowScrollButtonsMobile
              TabIndicatorProps={{ style: { display: "none" } }}
              className="tabList"
              onChange={handleChange}
              aria-label="Dynamic Tabs"
              variant="scrollable"
              value={uiTabValue}
            >
              {/* Hidden default "menu" tab (keeps TabContext happy) */}
              <Tab
                value="menu"
                label="menu"
                disableRipple={true}
                className="selectedTabDefault"
              />

              {/* Dynamic tabs created by menu clicks / deep-links */}
              {tabs.map((tab) => {
                const rawLabel = tab.label || "";

                return (
                  <Tab
                    key={tab.value}
                    disableRipple={true}
                    value={tab.value}
                    // Middle-click closes the tab (same UX as browser tabs)
                    onMouseDown={(event) => {
                      if (event.button === 1) {
                        document.body.onmousedown = (e) => {
                          if (e.button === 1) return false;
                        };
                        handleTabClose(tab.value);
                      }
                    }}
                    // Custom label: width + ellipsis + optional marquee
                    label={<TabLabel text={rawLabel} />}
                    icon={
                      <Close
                        className="tabCloseIcon"
                        onClick={(e) => {
                          // Prevent the close icon click from also selecting the tab
                          e.stopPropagation();
                          handleTabClose(tab.value);
                        }}
                      />
                    }
                    iconPosition="end"
                  />
                );
              })}
            </TabList>
          </TabContext>
        </Grid>

        {/* Right side: account / shipper badges */}
        <Grid size={{ xs: 0.2, sm: 0.5, md: 0.5, lg: 2.0 }}>
          <div className="divAccount" style={{ position: "relative" }}>
            <span className="accountInfo">
              {IS_CARRIER ? (
                <>
                  {carrierAccountName || ""}
                  <span className="accountInfoSeparator"> | </span>
                  {/* Carrier name is hidden on very small screens for space */}
                  <span className="shipperInfo">{carrierName || ""}</span>
                </>
              ) : (
                <>
                  {defaultAccountName || ""}
                  <span className="accountInfoSeparator"> | </span>
                  {/* Default shipper is hidden on very small screens for space */}
                  <span className="shipperInfo">
                    {defaultShipperName || ""}
                  </span>
                </>
              )}
            </span>
          </div>
        </Grid>
      </Grid>

      {/* ---------------------------------------------------------------------
         Lateral menu + main content shell
         - Left: fixed MLateralMenu (handles scroll inside the menu itself)
         - Right: "homepageContent" column (logos + routed pages)
         ------------------------------------------------------------------ */}
      <MLateralMenu
        handleChangeLng={handleChangeLng}
        handleLogout={handleLogout}
        handleSettings={handleSettings}
        handleMenuPages={handleMenuPages}
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        menus={filteredMenus}
        setMenus={setMenus}
        bodyPage={
          <div className="homeContentShell">
            {/* Background landing: logos + footer, fixed behind pages.
                Only rendered when showButtons = true (no tabs open). */}
            {showButtons && (
              <div className="homeLandingLayer">
                <div className="containerCentral">
                  <div className="logoBox">
                    <img
                      src="./images/LogoShipperForm.png"
                      alt="ShipperformLogo"
                    />
                  </div>
                  <div className="footerBox">
                    <img src="./images/nexus.png" alt="NexusLogo" />
                  </div>
                </div>
              </div>
            )}

            {/* Routed pages:
                - Absolute layer on top of the landing background
                - This is the ONLY area that scrolls vertically */}
            <div className="homeRoutesLayer">
              <div className="baseLine">
                <ScrollToTop
                  smooth
                  component={
                    <ArrowUpwardIcon
                      fontSize="large"
                      style={{ color: "var(--color-white)" }}
                    />
                  }
                  style={{
                    backgroundColor: `${ctx.primaryColor ? ctx.primaryColor : "var(--color-black)"
                      } !important`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "45px",
                    height: "45px",
                  }}
                />

                <Suspense fallback={<LoadingBar />}>
                  {/* Routes relative to BASE:
                      - user    -> /home/...
                      - carrier -> /carrier/home/... */}
                  <Routes>
                    {/* Shared & admin routes */}
                    <Route path="/changeLog" element={<ChangeLogPage />} />
                    <Route
                      path="/settings"
                      element={
                        <SettingsPage
                          roles={roles}
                          checkPermissions={checkPermissions}
                        />
                      }
                    />
                    <Route
                      path="/UserConfig"
                      element={
                        <UserConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/RoleConfig"
                      element={
                        <RoleConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Accounts */}
                    <Route
                      path="/accounts/*"
                      element={
                        <AccountPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/accounts/accountConfigCrud"
                      element={
                        <AccountConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Contacts */}
                    <Route
                      path="/contacts/*"
                      element={
                        <ContactPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/contacts/contactConfigCrud"
                      element={
                        <ContactConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Shippers */}
                    <Route
                      path="/shippers/*"
                      element={
                        <ShipperConfigPage
                          roles={roles}
                          onCreateTab={goToPage}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/shipper-markets/*"
                      element={
                        <ShipperMarketManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/shipper-carriers/*"
                      element={
                        <ShipperCarrierManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/shippers/shipperConfigCrud"
                      element={
                        <ShipperConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Carriers */}
                    <Route
                      path="/carriers"
                      element={
                        <CarrierConfigPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/carrier-markets"
                      element={
                        <CarrierMarketManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/carrier-typesOfService"
                      element={
                        <CarrierTypeOfServiceManagementPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/carrier-logins"
                      element={
                        <CarrierLoginsConfigPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/carriers/carrierConfigCrud"
                      element={
                        <CarrierConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/carriers/carrierLoginsConfigCrud"
                      element={
                        <CarrierLoginsConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Markets */}
                    <Route
                      path="/markets/*"
                      element={
                        <MarketPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/markets/marketConfigCrud"
                      element={
                        <MarketConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Locations */}
                    <Route
                      path="/locations/*"
                      element={
                        <LocationPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/locations/locationConfigCrud"
                      element={
                        <LocationConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Countries / Continents */}
                    <Route
                      path="/countries/*"
                      element={
                        <CountryPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/countries/CountryConfigCrud"
                      element={
                        <CountryConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/countries/ContinentConfigCrud"
                      element={
                        <ContinentConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Currencies */}
                    <Route
                      path="/currencies/*"
                      element={
                        <CurrencyPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/currencies/currencyConfigCrud"
                      element={
                        <CurrencyConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Transport Types */}
                    <Route
                      path="/transport-types/*"
                      element={
                        <TransportTypePage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/transport-types/transportTypeConfigCrud"
                      element={
                        <TransportTypeConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Lanes */}
                    <Route
                      path="/lanes/*"
                      element={
                        <LanePage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/lanes/laneConfigCrud"
                      element={
                        <LaneConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Routes */}
                    <Route
                      path="/routes/*"
                      element={
                        <RoutePage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/routes/routeConfigCrud"
                      element={
                        <RouteConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/routes/routeLaneConfigCrud"
                      element={
                        <RouteLaneConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Instant Quotes */}
                    <Route
                      path="/instant-quotes/*"
                      element={
                        <InstantQuotesSearchPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/instant-quotes/instant-quotesConfigCrud"
                      element={
                        <InstantQuotesSearchConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/instant-quotes/instantQuotesConfigCrud"
                      element={
                        <InstantQuotesConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Tenders - Spot / Non-Spot (realm-aware) */}
                    <Route
                      path="/tenders/spot-tenders/*"
                      element={
                        IS_CARRIER ? (
                          <SpotTenderCarrierInvitePage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        ) : (
                          <SpotTenderPage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        )
                      }
                    />

                    <Route
                      path="/tenders/non-spot-tenders/*"
                      element={
                        IS_CARRIER ? (
                          <NonSpotTenderCarrierInvitePage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        ) : (
                          <NonSpotTenderPage
                            roles={roles}
                            onCreateTab={goToPage}
                            handleTabClose={handleTabClose}
                            toastMessages={ctx.toastMessages}
                          />
                        )
                      }
                    />

                    <Route
                      path="/create-tenders/*"
                      element={
                        <CreateTenderPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/tenders/TenderInQuotation"
                      element={
                        <TenderInQuotationPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/tenders/non-spot-tenders/tenderPhaseConfig"
                      element={
                        <TenderPhaseConfig
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/tenders/tenderFinished"
                      element={
                        <TenderFinishedPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* Addresses */}
                    <Route
                      path="/addresses/*"
                      element={
                        <AddressPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                    <Route
                      path="/addresses/AddressConfigCrud"
                      element={
                        <AddressConfigCrud
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* API Keys */}
                    <Route
                      path="/ApiKeyConfig"
                      element={
                        <ApiKeyConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* TenderConfigCrud (user realm) */}
                    <Route
                      path="/tenders/TenderConfigCrud"
                      element={
                        <TenderConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                          isCarrierRealm={false}
                        />
                      }
                    />

                    {/* CarrierSubmitPage (standalone, legacy flow) */}
                    <Route
                      path="/tenders/CarrierSubmitPage"
                      element={
                        <CarrierSubmitPage
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    {/* CarrierInviteViewBid wrapper:
                        - entry point for carrier invite links
                        - coordinates TenderConfigCrud + CarrierSubmitPage */}
                    <Route
                      path="/CarrierInviteViewBid"
                      element={
                        <CarrierInviteViewBidPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                          onForceLogout={handleLogout}
                        />
                      }
                    />

                    {/* Tracking */}
                    <Route
                      path="/tracking/*"
                      element={
                        <TrackingPage
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />

                    <Route
                      path="/tracking/trackingConfigCrud"
                      element={
                        <TrackingConfigCrud
                          roles={roles}
                          onCreateTab={goToPage}
                          handleTabClose={handleTabClose}
                          toastMessages={ctx.toastMessages}
                        />
                      }
                    />
                  </Routes>
                </Suspense>
              </div>
            </div>
          </div>
        }
      />
    </NavigateContext.Provider>
  );
}

TabsHomePage.propTypes = {
  roles: PropTypes.array,
};
